/* eslint-disable arrow-body-style */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl, useSelector } from 'umi';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { addChartLabel, xAxisHighLight, xAxisLabelFormat } from '@/pages/dashboard/components/Chart/_config/plug';
import CardBox from '@/pages/dashboard/components/CardBox';
import { useLocal, useOption, useSelectorMap, useWeekDay, useWeekDayArr } from '@/pages/dashboard/utils/customHook';
import { commonDownloadValueFormatter, commonValueFormatter, getWeekDayTitle, handleDownload } from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { defaultSetOtherConfigFunc } from '../../_config/utils';
import { barFormatterFunc, pieFormatterFunc } from '@/pages/dashboard/components/Chart/_config/utils';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import { getSubTitleDate, momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from '@/pages/home/_config/config';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { getBaseTooltip } from '@/pages/dashboard/components/EchartsTooltip/utils';
import { getName } from '@home/utils/common';
import classNames from 'classnames';
import { dynamicString } from '@home/utils/dynamicString';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { getDataByKey } from '@/pages/dashboard/components/TrendChartCo/_utils/common';

interface DauItem {
  date: string;
  sdlw_date?: string;
  today?: number;
  sdlw?: number;
}

interface PcuAcuItem {
  date: string;
  pcu?: number;
  acu?: number;
}

interface DauBreakdownItem {
  date: string;
  total?: number;
  new_users?: number;
  retention_users?: number;
  return_users?: number;
}

interface KvListItem {
  key: string;
  value: number | string;
}

interface ChannelSystemItem {
  date: string;
  kv_list: KvListItem[];
}

export default function ActiveUsersCardCo(props: any) {
  const {
    config: propsConfig,
    data: propsData,
    isSingle = true,
    titleEn,
    titleCn,
    subtitle,
    customTooltipMap = {},
    className,
    customLegendMap = {},
    yAxisMin = { enabled: false, minRatio: 0 },
    yAxisFormat,
    customTableColumns = {},
    apiDataKey="keyMetricsData"
  } = props;
  const { data: defaultData, loading, errorMsg: error } = getDataByKey(apiDataKey, propsData) || {};
  const {
    type,
    defaultAvtive,
    componentMetrics,
    tooltipConfig,
    // 新增：统一配置 series 名称（趋势图、浮窗、表格、下载都用这个）
    seriesConfig,     // series 名称配置，支持 dynamicString
    // 数据相关配置
    dataKeyConfig,    // 数据字段 key 映射配置 { dataKey: 'pcu_acu', index: 1, seriesKeys: ['PCU', 'ACU'], keyMapping: { 'PCU': 'pcu', 'ACU': 'acu' } }
    // 以下为兼容老配置
    legendConfig,     // legend 名称配置（如不配置则用 seriesConfig）
    tableHeaderConfig, // 表格列头名称配置（如不配置则用 seriesConfig）
    // 新增：完全自定义配置（不依赖 type）
    chartConfig,      // 图表配置：{ showEyes: true, showPie: false, isStack: false }
  } = propsConfig || {};
  const { type: valueType = 'int', decimal = 0, formatRule } = componentMetrics || {};

  // 统一格式化函数：优先使用 formatRule，否则使用 commonValueFormatter
  // 注意：formatRule 需要使用 propsData 解析（包含 metricsInfo 等上下文）
  const formatValue = (value: string | number) => {
    if (formatRule) {
      return dataFormat({ value, formatRule: dynamicString(formatRule, propsData) });
    }
    return commonValueFormatter(valueType, value, decimal);
  };

  // 统一下载格式化函数：优先使用 formatRule，否则使用 commonDownloadValueFormatter
  const formatDownloadValue = (value: string | number) => {
    if (formatRule) {
      return dataFormat({ value, formatRule: dynamicString(formatRule, propsData) });
    }
    return commonDownloadValueFormatter(valueType, value);
  };
  const { gameCode } = useSelector((state: any) => ({
    gameCode: state?.dashboard?.currentGame?.game_code,
  }));
  const { formatMessage } = useIntl();
  const { isEN, locale, getLocalText } = useLocal('engagement_active_activeUsers', '_', gameCode);
  const contextObj = useContext(FilterContext);
  const { filterVal } = contextObj || {};
  const { timeType = 'Day', diff, rangeTime, ...fiters } = filterVal || {};
  const formValue = { timeType, filterValue: { rangeTime, ...fiters } };
  const { getSelectorText } = useSelectorMap();
  const { title, sumName } = useMemo(() => {
    const titleMap: any = (type: string) => {
      if (type === 'dau') return getLocalText(`${timeType?.toLowerCase()}_au_title`);
      if (type === 'pcu_acu') return getLocalText('pcu_acu_title');
      if (type === 'dau_breakdown') return getLocalText(`${timeType?.toLowerCase()}_au_breakdown_title`);
      if (type === 'dau_channels') return getLocalText(`${timeType?.toLowerCase()}_channel_distribution_title`);
      if (type === 'dau_systems') return getLocalText(`${timeType?.toLowerCase()}_platform_distribution_title`);
    };
    const titleResultObj: any = {
      day: componentMetrics?.[isEN ? 'enName' : 'cnName'],
      week: componentMetrics?.[isEN ? 'enWeekName' : 'cnWeekName'],
      month: componentMetrics?.[isEN ? 'enMonthName' : 'cnMonthName'],
    };
    const sumResultObj: any = {
      day: componentMetrics?.[isEN ? 'sumEnName' : 'sumCnName'],
      week: componentMetrics?.[isEN ? 'sumEnWeekName' : 'sumCnWeekName'],
      month: componentMetrics?.[isEN ? 'sumEnMonthName' : 'sumCnMonthName'],
    };
    // 使用 propsData 来解析 dynamicString，因为 propsData 包含 metricsInfo 等上下文数据
    return {
      title: getName(isEN, dynamicString(titleEn, propsData), dynamicString(titleCn, propsData)) || dynamicString(titleResultObj?.[timeType?.toLocaleLowerCase()], propsData) || titleMap(type),
      sumName: dynamicString(sumResultObj?.[timeType?.toLocaleLowerCase()], propsData) || ''
    };
  }, [locale, timeType, type, componentMetrics, titleEn, titleCn, isEN, propsData]);
  const [data, setData] = useState<{
    dau: DauItem[];
    pcu_acu: PcuAcuItem[];
    dau_breakdown: DauBreakdownItem[];
    dau_channels: ChannelSystemItem[];
    dau_systems: ChannelSystemItem[];
  }>({
    dau: [],
    pcu_acu: [],
    dau_breakdown: [],
    dau_channels: [],
    dau_systems: [],
  });
  const [chartType, setChartType] = useState('');
  const { getWeekDay } = useWeekDay();
  const weekDayArr = useWeekDayArr();
  const hasMonth = timeType === 'Month';
  const [pieData, setPieData] = useState({
    legend: [] as String[],
    data: [] as Array<{}>
  });
  const [iseye, setIseye] = useState(true);
  const NAME_MAP: any = {
    Day: ['Today', 'SDLW', 'DAU'],
    Week: ['This week', 'SWLM', 'WAU'],
    Month: ['This Month', 'SMLY', 'MAU']
  };

  // 获取配置的名称（支持 dynamicString）
  // 优先级：legendConfig/seriesConfig > customLegendMap > NAME_MAP 默认值
  const getConfiguredName = (
    configObj: any,
    timeKey: string,
    index: number,
    defaultName: string,
    dataSource: any = propsData
  ) => {
    // 1. 优先使用新的配置（支持 dynamicString）
    if (configObj) {
      // 先尝试按 timeKey 获取配置
      let config = configObj?.[timeKey];
      // 如果没有按 timeKey 配置，尝试获取通用配置（直接是数组或带 en/cn 的对象）
      if (!config && (Array.isArray(configObj) || (configObj.en || configObj.cn))) {
        config = configObj;
      }
      
      if (config) {
        // 支持数组格式：[name1, name2, name3]
        if (Array.isArray(config)) {
          const rawName = config[index];
          if (rawName) {
            return dynamicString(rawName, dataSource, rawName);
          }
        }
        // 支持对象格式：{ en: [name1, name2], cn: [name1, name2] }
        if (typeof config === 'object' && !Array.isArray(config)) {
          const names = isEN ? config.en : config.cn;
          const rawName = names?.[index];
          if (rawName) {
            return dynamicString(rawName, dataSource, rawName);
          }
        }
      }
    }
    // 2. 回退到默认名称
    return defaultName;
  };

  // 外部配置浮窗名称 - 支持中英文配置
  const getTooltipName = (timeKey: string, index: number, defaultName: string) => {
    // 优先使用 seriesConfig（统一配置）
    const configuredName = getConfiguredName(seriesConfig || legendConfig, timeKey, index, '', propsData);
    if (configuredName) {
      return configuredName;
    }

    const customConfig = customTooltipMap?.[timeKey];
    if (customConfig) {
      // 支持数组格式：[name1, name2, name3]
      if (Array.isArray(customConfig)) {
        return customConfig[index] || defaultName;
      }
      // 支持对象格式：{ en: [name1, name2], cn: [name1, name2] }
      if (typeof customConfig === 'object' && !Array.isArray(customConfig)) {
        const names = isEN ? customConfig.en : customConfig.cn;
        return names?.[index] || defaultName;
      }
    }
    return defaultName;
  };

  // 外部配置 legend 名称 - 优先级高于默认与国际化
  const getLegendName = (timeKey: string, index: number, defaultName: string) => {
    // 优先使用 seriesConfig（统一配置）
    const configuredName = getConfiguredName(seriesConfig || legendConfig, timeKey, index, '', propsData);
    if (configuredName) {
      return configuredName;
    }

    const customConfig = customLegendMap?.[timeKey];
    if (customConfig) {
      if (Array.isArray(customConfig)) {
        return customConfig[index] || defaultName;
      }
      if (typeof customConfig === 'object' && !Array.isArray(customConfig)) {
        const names = isEN ? customConfig.en : customConfig.cn;
        return names?.[index] || defaultName;
      }
    }
    // 若未配置 legend，则回退到 tooltip 的映射（保持两者一致的场景）
    return getTooltipName(timeKey, index, defaultName);
  };

  // 获取 series 名称（统一使用 seriesConfig）
  const getSeriesName = (timeKey: string, index: number, defaultName: string) => {
    const configuredName = getConfiguredName(seriesConfig || legendConfig, timeKey, index, '', propsData);
    if (configuredName) {
      return configuredName;
    }
    return getLegendName(timeKey, index, defaultName);
  };

  // 获取表格列头名称（统一使用 seriesConfig）
  const getTableHeaderName = (timeKey: string, index: number, defaultName: string) => {
    const configuredName = getConfiguredName(tableHeaderConfig || seriesConfig || legendConfig, timeKey, index, '', propsData);
    if (configuredName) {
      return configuredName;
    }
    return getLegendName(timeKey, index, defaultName);
  };

  useEffect(() => {
    const data = (() => {
      const resultData = JSON.parse(JSON.stringify(defaultData || {}));
      resultData?.dau_channels?.forEach((dataItem: { kv_list: any[]; }) => {
        dataItem?.kv_list?.forEach(legendItem => {
          legendItem.key = getSelectorText('channel', legendItem.key);
        });
      });
      resultData?.dau_systems?.forEach((dataItem: { kv_list: any[]; }) => {
        dataItem?.kv_list?.forEach(legendItem => {
          legendItem.key = getSelectorText('system', legendItem.key);
        });
      });
      return resultData;
    })();
    data && setData(data);
    isSingle ? handlePieData(data) : handleLongPieData(data);
  }, [defaultData, locale, isSingle]);

  const getSumValue = (arr: any[] = []) => {
    const sumValue = arr?.reduce((pre, cur) => {
      if (!isNaN(parseFloat(cur?.value))) pre += parseFloat(cur?.value);
      return pre;
    }, 0);
    return (sumValue && formatValue(sumValue)) || '0';
  };

  const getDateColumnTableOptions = (key: string) => {
    return {
      key,
      align: 'left',
      fixed: 'left',
      width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
      header: getLocalText(key),
      render: (cvm) => {
        const weekDay = getWeekDay(cvm?.[key]);
        const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
        return <>
          <span className='table-text'>{cvm?.[key]}</span>
          {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
        </>;
      }
    };
  };

  function getOptions(data: any[], index: number, timeType: string, _, module?: string) {
    const chart: any[] = [];
    const table: any = {
      option: [
        getDateColumnTableOptions('date'),
      ],
      data: [],
      downloadData: []
    };

    if (!Array.isArray(data) || data?.length === 0) {
      return {
        chart, table
      } as any;
    }

    // 默认的 legendList，支持通过 dataKeyConfig.seriesKeys 或 seriesConfig 配置覆盖
    const defaultLegendList = [
      [NAME_MAP[timeType][0], NAME_MAP[timeType][1]],
      ['PCU', 'ACU'],
      ['DAU', 'New Users', 'Retention Users', 'Return Users'],
      []
    ];

    // 优先使用 dataKeyConfig.seriesKeys 配置自定义 series 列表
    let currentLegendList: string[];
    if (dataKeyConfig?.seriesKeys) {
      currentLegendList = dataKeyConfig.seriesKeys.map((item: string) => dynamicString(item, propsData, item));
    } else if (index === 3) {
      // kv_list 类型的数据
      currentLegendList = data?.[0]?.kv_list?.map((item: { key: any; }) => item?.key) || [];
    } else {
      currentLegendList = defaultLegendList[index] || [];
    }

    currentLegendList?.forEach((name: string, legendIndex: number) => {
      // 获取自定义名称（legend 优先，其次 tooltip，最后使用国际化/默认名称）
      const defaultHeaderName = module ? getLocalText(`${module}_legend${(type === 'dau' && legendIndex === 1) ? legendIndex + 2 : legendIndex + 1}`) : name;
      // 使用 getSeriesName 获取 series 名称
      const customSeriesName = getSeriesName(timeType, legendIndex, defaultHeaderName);
      // 使用 getTableHeaderName 获取表头名称
      const customHeaderName = getTableHeaderName(timeType, legendIndex, defaultHeaderName);
      
      // 判断是否堆叠：优先使用 chartConfig.isStack 配置，否则根据 index 判断
      const isStack = chartConfig?.isStack !== undefined 
        ? chartConfig.isStack 
        : ![0, 1].includes(index);
      
      const seriesItem = {
        name: customSeriesName,  // 使用处理后的名称
        type: 'bar',
        stack: isStack ? 'bar' : '',
        data: [] as any[]
      };
      const thead: any = {
        key: '',
        header: customHeaderName,  // 表头使用 getTableHeaderName
        align: 'right',
        // width: 100,
        width: 80 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT
      };

      // 将 name 转换为数据 key，支持通过 dataKeyConfig.keyMapping 配置
      let dataKey = name;
      if (dataKeyConfig?.keyMapping?.[name]) {
        dataKey = dataKeyConfig.keyMapping[name];
      } else if (!dataKeyConfig?.seriesKeys) {
        // 只有在没有自定义 seriesKeys 时才使用默认转换逻辑
        if (index === 0) {
          dataKey = name.includes('L') ? 'sdlw' : 'today';
        } else {
          dataKey = index === 2 && name === 'DAU' ? 'total' : name;
          dataKey = [1, 2].includes(index) ? dataKey?.toLowerCase()?.replace(/\s/g, '_') : dataKey;
        }
      }

      // 图表
      // 判断是否需要保留小数：如果配置了 formatRule 且包含 'Decimal' 或 'percent'，则保留原始值
      const shouldKeepDecimal = formatRule && (
        formatRule.includes('Decimal') || 
        formatRule.includes('decimal') || 
        formatRule.includes('percent') ||
        formatRule.includes('Percent')
      );
      
      for (let i = 0;i < data?.length;i++) {
        if (index === 2 && dataKey === 'total') {
          continue;
        }

        const item = data[i];

        const date = hasMonth ? momentFormat(item.date, 'YYYY-MM') : getWeekDayTitle(item.date, weekDayArr);
        if (index === 3 || item.kv_list) {
          // kv_list 类型的数据
          item.kv_list?.forEach((obj: { key: any; value: number; }) => {
            if (name === obj.key) {
              const rawValue = obj.value;
              seriesItem?.data?.push({
                x: date,
                y: shouldKeepDecimal ? rawValue : (Math.round(rawValue) || (!rawValue ? rawValue : String(+rawValue))),
              });
            }
          });
        } else {
          const rawValue = item[dataKey];
          seriesItem?.data?.push({
            x: date,
            y: shouldKeepDecimal ? rawValue : (Math.round(rawValue) || rawValue),
          });
        }
      }
      dataKey !== 'total' && chart.push(seriesItem);
      // 表头数据
      thead.key = dataKey;
      thead.render = (row: any) => <span>{row[dataKey]}</span>;
      if (sumName && dataKey === 'total') {
        thead.header = sumName;
      }
      table.option.push(thead);
    });

    const customColumns = customTableColumns?.[timeType] || [];
    if (customColumns.length > 0) {
      customColumns.forEach((columnConfig) => {
        const { key, position, render, renderParams } = columnConfig;
        let columnOption: any;
        if (render === 'getDateColumnTableOptions') {
          columnOption = getDateColumnTableOptions(key);
          if (renderParams) {
            columnOption = {
              ...columnOption, 
              ...renderParams,
              header: isEN ? renderParams.header.en : renderParams.header.cn,
            };
          }
        }
        const insertPosition = (position || 0);
        table.option.splice(insertPosition, 0, columnOption);
      });
    }

    // 表格数据
    if (index === 3) {
      for (let i = 0;i < data.length;i++) {
        const item = data[i];
        const tableDate = hasMonth ? momentFormat(item.date, 'YYYY-MM') : momentFormat(item.date, 'YYYY-MM-DD');
        const tdata: any = { date: tableDate };
        const tdownloadData: any = { date: tableDate };
        item.kv_list.forEach((obj: { key: string | number; value: string | number; }) => {
          tdata[obj.key] = `${formatValue(obj.value)}`;
          tdownloadData[obj.key] = formatDownloadValue(obj.value);
          // 替换表头显示名（仅当 option 已创建对应 thead 时，thead.header 已按 legend 名设置）
        });
        table.data[i] = tdata;
        table.downloadData[i] = tdownloadData;
      }
    } else {
      const keys = Object.keys(data?.[0]);
      data?.forEach((item) => {
        const temp: any = {};
        const downloadTemp: any = {};
        keys.forEach((k) => {
          let v = item[k];
          if (k === 'date' || (customTableColumns?.[timeType]?.length > 0 && k.includes('date'))) {
            v = hasMonth ? momentFormat(v, 'YYYY-MM') : getWeekDayTitle(v, weekDayArr);
            temp[k] = hasMonth ? momentFormat(v, 'YYYY-MM') : momentFormat(v, 'YYYY-MM-DD');
            downloadTemp[k] = hasMonth ? momentFormat(v, 'YYYY-MM') : momentFormat(v, 'YYYY-MM-DD');
          } else {
            temp[k] = `${formatValue(v)}`;
            downloadTemp[k] = formatDownloadValue(v);
          }
        });
        table.data.push(temp);
        table.downloadData.push(downloadTemp);
      });
    }

    return {
      chart, table
    } as any;
  }

  function handleAxisFormat(val: string) {
    return timeType === 'Month' ? val : momentFormat(val.split("(")[0], 'MM-DD');
  }

  function handlePieData(origin: any) {
    const data: Array<{}> = [];
    const legend: String[] = [];
    const lastList: any[] = origin?.[type]?.[origin?.[type]?.length - 1]?.kv_list ?? [];
    const bool: boolean = lastList.length === lastList.filter(e => e.value === '').length;

    !bool && lastList.forEach(({ key, value }, idx: number) => {
      const displayName = getLegendName(timeType, idx, key);
      data.push({ name: displayName as any, value });
      legend.push(displayName as any);
    });

    setPieData({ legend, data });
  }

  function handleLongPieData(origin: any) {
    const data: Array<{}> = [];
    const legend: String[] = [];
    const dataList = origin.distinct_dau_system_pie || [];
    dataList?.map((item: any, idx: number) => {
      const name = getSelectorText('system', item?.full_name);
      const displayName = getLegendName(timeType, idx, name);
      data.push({ name: displayName as any, value: item?.value });
      legend.push(displayName as any);
    });
    setPieData({ legend, data });
  }

  const distributionConfig = {
    bar: {
      formatterFunc: (params: any[]) => {
        if (tooltipConfig) {
          return getTooltipText(params, sumName ? sumName : getLocalText(`${timeType?.toLowerCase()}_au_breakdown_sum_row`));
        }
        return barFormatterFunc(params, {
          isSort: true,
          // isRemoveNull: true,
        });
      }
    },
    line: {
      formatterFunc: (params: any[]) => {
        if (tooltipConfig) {
          return getTooltipText(params, sumName ? sumName : getLocalText(`${timeType?.toLowerCase()}_au_breakdown_sum_row`));
        }
        return barFormatterFunc(params, {
          isSort: true,
          // isRemoveNull: true,
        });
      }
    },
  };

  const getConfig = (module: string, setOptions?: (options: any) => void) => {
    function formatterFunc(list: any[]) {
      // 直接通过 dataIndex 获取对应的原始数据
      const dataIndex = list?.[0]?.dataIndex;
      const dauData = data?.dau || [];
      const compareDate = dauData?.[dataIndex]?.sdlw_date || "";
      const sdlwDate = getWeekDayTitle(compareDate, weekDayArr);
      const temp: any = {
        title: list[0].name,
        metricsName: name,
        data: [] as Array<{}>
      };
      list.forEach((ele: { value: any; color: any; }, index: number) => {
        const name = getTooltipName(timeType, index, getLocalText(`${module}_legend${(type === 'dau' && index === 1) ? index + 2 : index + 1}`));
        const item: any = {
          name,
          metricsNum: formatValue(ele.value),
          color: ele.color,
        };
        // 临时处理展示去年同期日期
        if (type === 'dau'  && compareDate && index ===1) {
          item.subTitle = sdlwDate;
        }
        temp.data.push(item);
      });
      return getEchartTooltipText(temp);
    }
    return {
      bar: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          defaultSetOtherConfigFunc({ opt, module, getLocalText });
          // 覆盖 legend 文案，优先使用外部 customLegendMap，其次 tooltipMap，最后默认/国际化
          opt.legend = opt.legend || {};
          opt.legend.formatter = (name: string) => {
            const idx = opt?.legend?.data?.findIndex((item: string) => item === name) ?? 0;
            const defaultName = getLocalText(`${module}_legend${(type === 'dau' && idx === 1) ? idx + 2 : idx + 1}`);
            return getLegendName(timeType, idx, defaultName);
          };
          setOptions?.(opt);
          return opt;
        }
      },
      line: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          defaultSetOtherConfigFunc({ opt, module, getLocalText });
          // 覆盖 legend 文案，优先使用外部 customLegendMap，其次 tooltipMap，最后默认/国际化
          opt.legend = opt.legend || {};
          opt.legend.formatter = (name: string) => {
            const idx = opt?.legend?.data?.findIndex((item: string) => item === name) ?? 0;
            const defaultName = getLocalText(`${module}_legend${(type === 'dau' && idx === 1) ? idx + 2 : idx + 1}`);
            return getLegendName(timeType, idx, defaultName);
          };
          setOptions?.(opt);
          return opt;
        }
      },
    };
  };

  // 浮窗
  const getTooltipText = (params: any, valueName: string) => {
    const {
      shareNameCn,
      shareNameEn,
      sumNameCn,
      sumNameEn,
      valueFormatRule: tooltipValueFormatRule,
    } = tooltipConfig || {};
    // 使用自定义 tooltip 名称映射
    const newParams = params?.map((p: any, i: number) => {
      const customName = getTooltipName(timeType, i, p.seriesName);
      return { ...p, seriesName: customName };
    });

    // 优先使用 tooltipConfig.valueFormatRule，其次使用 componentMetrics.formatRule
    const resolvedValueFormatRule = tooltipValueFormatRule
      ? dynamicString(tooltipValueFormatRule, propsData)
      : (formatRule ? dynamicString(formatRule, propsData) : undefined);

    return getBaseTooltip({
      params: newParams,
      options: {
        isEn: isEN,
        valueNameCn: valueName,
        valueNameEn: valueName,
        shareNameCn,
        shareNameEn,
        sumNameCn,
        sumNameEn,
        ...(resolvedValueFormatRule && { valueFormatRule: resolvedValueFormatRule }),
      }
    });
  };

  const breakdownFormatterFunc = (params: any[] | undefined) => {
    if (tooltipConfig) {
      const {
        shareNameCn,
        shareNameEn,
        sumNameCn,
        sumNameEn,
        valueFormatRule: tooltipValueFormatRule,
      } = tooltipConfig || {};
      // 使用自定义名称或默认名称，index + 1 跳过 seriesConfig[0]（total/DAU）
      const newParams = params?.map((item, index) => {
        const customName = getTooltipName(timeType, index + 1, getLocalText(`${timeType?.toLowerCase()}_au_breakdown_legend${index + 2}`));
        return { ...item, seriesName: customName };
      });
      const valueName = sumName ? sumName : getLocalText(`${timeType?.toLowerCase()}_au_breakdown_sum_row`);
      const resolvedValueFormatRule = tooltipValueFormatRule
        ? dynamicString(tooltipValueFormatRule, propsData)
        : (formatRule ? dynamicString(formatRule, propsData) : undefined);
      // 直接调用 getBaseTooltip，避免 getTooltipText 中从 index=0 开始的二次名称映射
      return getBaseTooltip({
        params: newParams,
        options: {
          isEn: isEN,
          valueNameCn: valueName,
          valueNameEn: valueName,
          shareNameCn,
          shareNameEn,
          sumNameCn,
          sumNameEn,
          ...(resolvedValueFormatRule && { valueFormatRule: resolvedValueFormatRule }),
        }
      });
    }
    return getEchartTooltipText({
      hasSum: true,
      sumName: sumName ? sumName : getLocalText(`${timeType?.toLowerCase()}_au_breakdown_sum_row`),
      sumNum: getSumValue(params),
      title: params?.[0].axisValue,
      data: params?.map(({ value, color }: any, index: number) => {
        const name = getTooltipName(timeType, index + 1, getLocalText(`${timeType?.toLowerCase()}_au_breakdown_legend${index + 2}`));
        return {
          name,
          metricsNum: formatValue(value),
          color,
        };
      }),
    });
  };
  
  const breakdownConfig = {
    bar: {
      formatterFunc: breakdownFormatterFunc,
      setOtherConfigFunc: (opt: any) => {
        defaultSetOtherConfigFunc({
          opt,
          module: `${timeType?.toLowerCase()}_au_breakdown`,
          indexGap: 2,
          getLocalText
        });
        // 覆盖 legend 文案，优先使用外部 customLegendMap，其次 tooltipMap，最后默认/国际化
        opt.legend = opt.legend || {};
        opt.legend.formatter = (name: string) => {
          const idx = opt?.legend?.data?.findIndex((item: string) => item === name) ?? 0;
          const defaultName = getLocalText(`${timeType?.toLowerCase()}_au_breakdown_legend${idx + 2}`);
          return getLegendName(timeType, idx + 1, defaultName);
        };
        return opt;
      }
    },
    line: {
      formatterFunc: breakdownFormatterFunc,
      setOtherConfigFunc: (opt: any) => {
        defaultSetOtherConfigFunc({
          opt,
          module: `${timeType?.toLowerCase()}_au_breakdown`,
          indexGap: 2,
          getLocalText
        });
        // 覆盖 legend 文案，优先使用外部 customLegendMap，其次 tooltipMap，最后默认/国际化
        opt.legend = opt.legend || {};
        opt.legend.formatter = (name: string) => {
          const idx = opt?.legend?.data?.findIndex((item: string) => item === name) ?? 0;
          const defaultName = getLocalText(`${timeType?.toLowerCase()}_au_breakdown_legend${idx + 2}`);
          return getLegendName(timeType, idx + 1, defaultName);
        };
        return opt;
      }
    },
  };

  // 根据 type 或 dataKeyConfig.dataKey 获取 index
  const getTypeIndex = (typeValue: string) => {
    const typeIndexMap: Record<string, number> = {
      dau: 0,
      pcu_acu: 1,
      dau_breakdown: 2,
      dau_channels: 3,
      dau_systems: 3,
    };
    return typeIndexMap[typeValue] ?? -1;
  };

  // 获取当前使用的 index
  const currentIndex = dataKeyConfig?.index ?? getTypeIndex(type);

  const option = (() => {
    // 优先使用 dataKeyConfig.dataKey 指定数据源
    const dataKey = dataKeyConfig?.dataKey || type;
    const dataSource = data?.[dataKey as keyof typeof data];
    
    // 确定 module 名称（用于国际化 key）
    const moduleMap: Record<string, string> = {
      dau: `${timeType?.toLowerCase()}_au`,
      pcu_acu: 'pcu_acu',
      dau_breakdown: `${timeType?.toLowerCase()}_au_breakdown`,
    };
    const module = dataKeyConfig?.module || moduleMap[type] || '';

    if (currentIndex >= 0 && dataSource) {
      return getOptions(dataSource as any[], currentIndex, timeType, false, module);
    }
    
    // 兼容老逻辑
    if (type === 'dau') return getOptions(data?.dau, 0, timeType, false, `${timeType?.toLowerCase()}_au`);
    if (type === 'pcu_acu') return getOptions(data?.pcu_acu, 1, timeType, false, 'pcu_acu');
    if (type === 'dau_breakdown') return getOptions(data?.dau_breakdown, 2, timeType, true, `${timeType?.toLowerCase()}_au_breakdown`);
    if (type === 'dau_channels') return getOptions(data?.dau_channels, 3, timeType, true);
    if (type === 'dau_systems') return getOptions(data?.dau_systems, 3, timeType, true);
  })();

  const isPieNodata = pieData?.data?.length === 0;
  const { xAxisData, legendData, lineSeries, barSeries, isNodata} = useOption(option);

  // 通用的 series 配置（用于新配置方式，支持 seriesConfig）
  const getGenericConfig = (moduleName?: string) => {
    const module = moduleName || dataKeyConfig?.module || '';
    function formatterFunc(list: any[]) {
      const temp: any = {
        title: list[0].name,
        metricsName: name,
        data: [] as Array<{}>
      };
      list.forEach((ele: { value: any; color: any; seriesName: string; }, index: number) => {
        // 优先使用 seriesConfig 配置的名称
        const defaultName = module ? getLocalText(`${module}_legend${index + 1}`) : ele.seriesName;
        const name = getTooltipName(timeType, index, defaultName);
        const item: any = {
          name,
          metricsNum: formatValue(ele.value),
          color: ele.color,
        };
        temp.data.push(item);
      });
      return getEchartTooltipText(temp);
    }
    return {
      bar: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          opt.legend = opt.legend || {};
          opt.legend.formatter = (name: string) => {
            const idx = opt?.legend?.data?.findIndex((item: string) => item === name) ?? 0;
            const defaultName = module ? getLocalText(`${module}_legend${idx + 1}`) : name;
            return getLegendName(timeType, idx, defaultName);
          };
          return opt;
        }
      },
      line: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          opt.legend = opt.legend || {};
          opt.legend.formatter = (name: string) => {
            const idx = opt?.legend?.data?.findIndex((item: string) => item === name) ?? 0;
            const defaultName = module ? getLocalText(`${module}_legend${idx + 1}`) : name;
            return getLegendName(timeType, idx, defaultName);
          };
          return opt;
        }
      },
    };
  };

  const config = (() => {
    // 优先判断老的 type 配置
    if (type === 'dau') return getConfig(`${timeType?.toLowerCase()}_au`, (opt) => {
      opt?.series?.forEach((item: { z: number; itemStyle: { normal: { lineStyle: { width: number; type: string; }; }; }; }, index: number) => {
        item.z = index === 0 ? 1 : 0;
        if (index === 1) {
          item.itemStyle = {
            normal: {
              lineStyle: {
                width: 2,
                type: 'dotted'
              }
            }
          };
        }
      });
    });
    if (type === 'pcu_acu') return getConfig('pcu_acu');
    if (type === 'dau_breakdown') return breakdownConfig;
    if (type === 'dau_channels') return distributionConfig;
    if (type === 'dau_systems') return distributionConfig;
    
    // 新配置方式：使用 seriesConfig 和 dataKeyConfig
    if (seriesConfig || dataKeyConfig) {
      return getGenericConfig(dataKeyConfig?.module);
    }
    
    return {
      bar: {
        formatterFunc: null,
        setOtherConfigFunc: (opt: any) => opt
      },
      line: {
        formatterFunc: null,
        setOtherConfigFunc: (opt: any) => opt
      },
    };
  })();

  // 判断是否显示眼睛图标：
  // 1. 优先使用 chartConfig.showEyes 配置
  // 2. 其次根据 type 判断（向后兼容）
  // 3. 最后根据 currentIndex 判断（新配置方式）
  const shouldShowEyes = chartConfig?.showEyes !== undefined 
    ? chartConfig.showEyes 
    : (type === 'dau' || type === 'pcu_acu' || (!type && (currentIndex === 0 || currentIndex === 1)));

  // 判断是否显示饼图：
  // 1. 优先使用 chartConfig.showPie 配置
  // 2. 其次根据 type 判断（向后兼容）
  const shouldShowPie = chartConfig?.showPie !== undefined
    ? chartConfig.showPie
    : (type === 'dau_channels' || type === 'dau_systems');
  
  const renderList = [
    shouldShowEyes && {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <Abnormal
        style={{ height: 340 }}
        component={<BarChart
          key={isEN}
          height={340}
          legendData={xAxisData.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData.length > 0 ? barSeries : []}
          formatterFunc={config?.bar?.formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            if (config?.bar?.setOtherConfigFunc) opt = config?.bar?.setOtherConfigFunc(opt);
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string; }) => handleAxisFormat(params.value)
            });
            if (shouldShowEyes) {
              opt = addChartLabel(opt, {
                formatter: (params: { value: string | number; }) => formatValue(params?.value),
                show: iseye,
                formatNum: (value: string | number) => formatValue(value)
              });
            }
            if (yAxisFormat) {
              opt.yAxis = {
                ...(opt.yAxis || {}),
                axisLabel: {
                  ...(opt.yAxis?.axisLabel || {}),
                  formatter: (value: number) =>
                    dataFormat({ value, formatRule: yAxisFormat }),
                },
              };
            }
            return opt;
          }}
        />}
        isLoading={loading}
        isError={error}
        isNoData={isNodata}
      />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <Abnormal
        style={{ height: 340 }}
        component={<LineChart
          key={isEN}
          height={340}
          legendData={xAxisData.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData.length > 0 ? lineSeries : []}
          formatterFunc={config?.line?.formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt);
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            if (shouldShowEyes) {
              opt = addChartLabel(opt, {
                formatter: (params: { value: string | number; }) => formatValue(params?.value),
                show: iseye,
                formatNum: (value: string | number) => formatValue(value)
              });
            }
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string; }) => handleAxisFormat(params.value)
            });
            if (yAxisFormat) {
              opt.yAxis = {
                ...(opt.yAxis || {}),
                axisLabel: {
                  ...(opt.yAxis?.axisLabel || {}),
                  formatter: (value: number) =>
                    dataFormat({
                      value,
                      formatRule: yAxisFormat,
                    }),
                },
              };
            }
            opt.yAxis.min = yAxisMin.enabled
              ? (value) => value.min - (value.max - value.min) * yAxisMin.minRatio
              : undefined;
            return opt;
          }}
        />}
        isLoading={loading}
        isError={error}
        isNoData={isNodata}
      />
    },
    shouldShowPie && {
      type: 'pieChart',
      svgType: 'pie-chart-icon',
      content: formatMessage({ id: 'common_pie_chart' }),
      component: <PieChart
        height={326}
        isNodata={isPieNodata}
        chartData={pieData?.data || []}
        legendData={pieData?.legend || []}
        formatterFunc={(params: any) => pieFormatterFunc(params, formValue, {
          isLastDate: isSingle,
          valueFormatRule: dynamicString(formatRule, propsData) || '',
        })}
      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        key={`table-${type || dataKeyConfig?.dataKey || 'default'}`}
        height={340}
        loading={false}
        tableOption={option?.table?.option}
        tableList={option?.table?.data}
      />
    },
  ]?.filter(v => v !== false);
  const startTime = momentFormat(rangeTime?.[0], hasMonth ? 'YYYY-MM' : 'YYYY-MM-DD');
  const endTime = momentFormat(rangeTime?.[1], hasMonth ? 'YYYY-MM' : 'YYYY-MM-DD');

  return (
    <CardBox
      className={classNames(className, 'register-table')}
      height={340}
      bordered={false}
      titleCom={title}
      subTitleCom={
        <>
          {subtitle && <div className='register-table-time'>{dynamicString(subtitle,{...propsData})}</div>}
          {shouldShowPie && chartType === 'pieChart' && (
            <div className='register-table-time'>
              {isSingle
                ? (hasMonth ? getSubTitleDate(formValue) : getWeekDayTitle(getSubTitleDate(formValue), weekDayArr, '~'))
                : `${startTime}~${endTime}`}
            </div>
          )}
        </>
      }
      setCurrentDataType={(type: any) => {
        setChartType(type);
        if (type === 'eyes') {
          setIseye(!iseye);
        }
      }}
      renderList={renderList || []}
      defaultAvtive={defaultAvtive !== undefined ? defaultAvtive : (type === 'dau_breakdown' ? 1 : 2)}
      downloadClick={async () => {
        handleDownload(option?.table, title, true, customTableColumns?.[timeType]?.filter((item: any) => item.key.includes('date'))?.length > 0);
      }}
      isLoading={loading}
      isError={error}
      isNodata={!isSingle && type === 'dau_systems' ? isPieNodata && isNodata : isNodata}
    />
  );
}
